/**
 * Cart — render bracelet previews (design JSON + bead line properties).
 */
(function () {
  const CART_UPDATE = 'cart:update';
  const CART_REFRESH = 'cart-bracelet:refresh';
  const resizeObservers = new WeakMap();
  const previewInstances = new WeakMap();
  const renderedContainers = new WeakSet();

  let scheduleTimer = null;

  function roundWristCm(cm) {
    const Core = window.BraceletCore;
    if (Core?.roundWristCm) return Core.roundWristCm(cm);
    const n = Number(cm);
    if (!Number.isFinite(n)) return 15;
    return Math.round(n * 100) / 100;
  }

  function parseWristCm(el, designJson, lineProps) {
    const fromJson = designJson?.w ?? designJson?.wrist_size_cm;
    if (fromJson) return roundWristCm(fromJson);
    const fromProps = lineProps?.['Wrist Size'];
    if (typeof fromProps === 'string') {
      const n = Number(fromProps.split(' ')[0]);
      if (Number.isFinite(n) && n > 0) return roundWristCm(n);
    }
    const n = Number(el?.dataset?.wristCm);
    return Number.isFinite(n) && n > 0 ? roundWristCm(n) : 15;
  }

  function readJsonScript(container, selector) {
    const el = container.querySelector(selector);
    if (!el?.textContent?.trim()) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch {
      return null;
    }
  }

  function parseDesignValue(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try {
        const once = JSON.parse(raw);
        if (typeof once === 'string') {
          try {
            return JSON.parse(once);
          } catch {
            return null;
          }
        }
        return once;
      } catch {
        return null;
      }
    }
    return null;
  }

  function readDesignJson(container) {
    const b64 = container.dataset.designB64;
    if (b64) {
      try {
        const binary = atob(b64.replace(/\s/g, ''));
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        const parsed = parseDesignValue(json);
        if (parsed) return parsed;
      } catch {
        /* fall through */
      }
    }
    return parseDesignValue(readJsonScript(container, '[data-cart-bracelet-design]'));
  }

  function normalizeLineProps(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (Array.isArray(raw)) {
      const out = {};
      for (const entry of raw) {
        if (entry?.first != null) out[entry.first] = entry.last;
        else if (entry?.[0] != null) out[entry[0]] = entry[1];
      }
      return Object.keys(out).length ? out : null;
    }
    return raw;
  }

  function linePropsFromContainer(container) {
    const fromScript = normalizeLineProps(readJsonScript(container, '[data-cart-line-properties]'));
    if (fromScript) return fromScript;

    const wristCm = container?.dataset?.wristCm;
    const beadCount = container?.dataset?.beadCount;
    const props = {};
    if (wristCm) {
      const Core = window.BraceletCore;
      const label = Core?.formatWristCm ? Core.formatWristCm(wristCm) : String(wristCm);
      props['Wrist Size'] = `${label} cm`;
    }
    if (beadCount) props['Bead Count'] = String(beadCount);
    return Object.keys(props).length ? props : null;
  }

  function renderSignature(container) {
    const lineKey = container.dataset.lineKey || '';
    const b64 = container.dataset.designB64 || '';
    return `${lineKey}|${b64.slice(0, 48)}|${container.dataset.beadCount || ''}`;
  }

  function formatEstimateMoney(amount) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '';
    const currency =
      window.Shopify?.currency?.active ||
      document.documentElement.getAttribute('data-shop-currency') ||
      'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  }

  function parseMoneyAmount(text) {
    const cleaned = String(text || '').replace(/[^\d0-9.,]/g, '');
    const normalized = cleaned.includes(',') && !cleaned.includes('.')
      ? cleaned.replace(',', '.')
      : cleaned.replace(/,/g, '');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }

  function looksLikeShellPrice(text) {
    const amount = parseMoneyAmount(text);
    return amount == null || amount <= 0.02;
  }

  function resolveDisplayTotal(container, beads) {
    const Core = window.BraceletCore;
    const fromBeads = Core && beads?.length ? Core.calcTotal(beads) : 0;
    const fromStored = Number(container?.dataset?.designTotalCents);
    const storedUsd = Number.isFinite(fromStored) && fromStored > 0 ? fromStored / 100 : 0;
    if (fromBeads >= 0.02) return fromBeads;
    if (storedUsd >= 0.02) return storedUsd;
    return 0;
  }

  function updateDesignEstimate(container, beads) {
    const card = container.closest('.cart-bracelet-card');
    const linePriceEl = card?.querySelector('.cart-bracelet-card__price');
    const estimateEl = card?.querySelector('[data-cart-bracelet-estimate]');
    if (!linePriceEl) return;

    const displayTotal = resolveDisplayTotal(container, beads);
    if (displayTotal < 0.02) {
      if (estimateEl) estimateEl.hidden = true;
      return;
    }

    const lineText = linePriceEl.textContent?.trim() || '';
    if (looksLikeShellPrice(lineText)) {
      linePriceEl.textContent = formatEstimateMoney(displayTotal);
      if (estimateEl) estimateEl.hidden = true;
    } else if (estimateEl) {
      estimateEl.hidden = true;
    }
  }

  function syncCartTotalDisplay() {
    const totalEl =
      document.querySelector('[data-testid="cart-total-value"]') ||
      document.querySelector('[data-cart-subtotal]');
    if (!totalEl || !looksLikeShellPrice(totalEl.textContent)) return;

    let cents = 0;
    document.querySelectorAll('[data-design-total-cents]').forEach((node) => {
      const value = Number(node.dataset.designTotalCents);
      if (Number.isFinite(value) && value > 0) cents += value;
    });
    if (cents <= 0) return;

    totalEl.textContent = formatEstimateMoney(cents / 100);
    if (totalEl.hasAttribute('value')) {
      totalEl.setAttribute('value', totalEl.textContent);
    }
  }

  function resolveBeads(Core, container) {
    const lineProps = linePropsFromContainer(container);
    let designJson = readDesignJson(container);

    if (!designJson && lineProps) {
      designJson = parseDesignValue(Core.parseDesignJsonFromProperties(lineProps));
      if (typeof designJson === 'string') {
        designJson = parseDesignValue(designJson);
      }
    }

    let beads = designJson ? Core.expandDesign(designJson) : [];
    if (!beads.length && lineProps) {
      beads = Core.expandDesignFromLineProperties(lineProps);
    }

    return { beads, designJson, lineProps };
  }

  function showError(container) {
    container.classList.add('is-error');
    container.classList.remove('is-ready', 'is-loading');
    const fb = container.querySelector('[data-cart-bracelet-fallback]');
    if (fb) fb.hidden = false;
    renderedContainers.delete(container);
    previewInstances.delete(container);
  }

  function markReady(container, beads) {
    container.classList.add('is-ready');
    container.classList.remove('is-error', 'is-loading');
    const fb = container.querySelector('[data-cart-bracelet-fallback]');
    if (fb) fb.hidden = true;
    updateDesignEstimate(container, beads);
    renderedContainers.add(container);
    container.dataset.renderSignature = renderSignature(container);
  }

  function refreshExistingPreview(container) {
    const state = previewInstances.get(container);
    if (!state?.preview) return false;
    state.preview.resize();
    updateDesignEstimate(container, state.beads);
    return true;
  }

  async function renderPreview(container, force) {
    if (!(container instanceof Element)) return;

    const sig = renderSignature(container);
    if (
      container.classList.contains('is-ready') &&
      renderedContainers.has(container) &&
      container.dataset.renderSignature === sig
    ) {
      refreshExistingPreview(container);
      return;
    }

    if (!force && renderedContainers.has(container) && container.dataset.renderSignature === sig) {
      refreshExistingPreview(container);
      return;
    }

    const Core = window.BraceletCore;
    if (!Core) return;

    const canvas = container.querySelector('canvas');
    if (!canvas) return;

    const lineProps = linePropsFromContainer(container);
    let designJson = readDesignJson(container);
    if (!designJson && lineProps) {
      designJson = Core.parseDesignJsonFromProperties(lineProps);
    }
    const wristCm = parseWristCm(container, designJson, lineProps);

    const { beads } = resolveBeads(Core, container);
    if (!beads.length) {
      showError(container);
      return;
    }

    const existing = previewInstances.get(container);
    if (existing?.preview && existing.signature === sig) {
      existing.preview.resize();
      markReady(container, beads);
      return;
    }

    container.classList.add('is-loading');
    container.classList.remove('is-error', 'is-ready');
    const fb = container.querySelector('[data-cart-bracelet-fallback]');
    if (fb) fb.hidden = true;

    try {
      const cfg = Core.CONFIG();
      const preview = new Core.BraceletCanvas(canvas);
      preview.brandName = '';
      preview.centerLogoUrl = cfg.centerLogoUrl || '';
      if (!preview.centerLogoUrl) preview.skipCenterBrand = true;
      preview.setTargetCircumferenceCm(wristCm);
      try {
        await Promise.all([
          preview.centerLogoUrl ? preview.preloadCenterLogo() : Promise.resolve(),
          preview.preloadImages(beads),
        ]);
      } catch (preloadErr) {
        console.warn('[cart-bracelet-preview] preload', preloadErr);
      }
      preview.setBeads(beads, { animate: false });
      previewInstances.set(container, { preview, beads, signature: sig });

      const prevObs = resizeObservers.get(container);
      prevObs?.disconnect();
      const ro = new ResizeObserver(() => {
        const inst = previewInstances.get(container);
        inst?.preview?.resize();
      });
      ro.observe(container);
      resizeObservers.set(container, ro);

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        markReady(container, beads);
      };

      const tryResize = (attempts) => {
        preview.resize();
        const size = preview.getDrawSize?.() ?? canvas.clientWidth;
        if (size < 8 && attempts > 0) {
          window.requestAnimationFrame(() => tryResize(attempts - 1));
          return;
        }
        finish();
      };
      tryResize(12);
    } catch (err) {
      console.warn('[cart-bracelet-preview]', err);
      showError(container);
    }
  }

  function init(force) {
    document.querySelectorAll('[data-cart-bracelet-preview]').forEach((container) => {
      if (force && container instanceof Element) {
        const sig = renderSignature(container);
        if (container.dataset.renderSignature !== sig) {
          renderedContainers.delete(container);
          previewInstances.delete(container);
        }
      }
      renderPreview(container, force).catch((err) => {
        console.warn('[cart-bracelet-preview]', err);
        showError(container);
      });
    });
    syncCartTotalDisplay();
  }

  function scheduleInit(force) {
    if (scheduleTimer) window.clearTimeout(scheduleTimer);
    scheduleTimer = window.setTimeout(() => {
      scheduleTimer = null;
      window.requestAnimationFrame(() => init(force));
    }, 120);
  }

  function mutationNeedsPreview(mutations) {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-cart-bracelet-preview]')) return true;
        if (node.querySelector?.('[data-cart-bracelet-preview]')) return true;
      }
    }
    return false;
  }

  let bootAttempts = 0;
  const BOOT_MAX = 100;

  function boot() {
    bootAttempts += 1;
    if (!window.BraceletCore) {
      if (bootAttempts < BOOT_MAX) window.setTimeout(boot, 80);
      return;
    }
    if (!Array.isArray(window.__BEADS__)) {
      window.__BEADS__ = [];
    }
    scheduleInit(false);
  }

  function watchCartDom() {
    const root = document.querySelector('cart-items-component');
    if (!root || root.dataset.braceletPreviewObs) return;
    root.dataset.braceletPreviewObs = '1';
    const obs = new MutationObserver((mutations) => {
      if (mutationNeedsPreview(mutations)) scheduleInit(false);
    });
    obs.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      watchCartDom();
    });
  } else {
    boot();
    watchCartDom();
  }

  document.addEventListener(CART_UPDATE, () => scheduleInit(false));
  document.addEventListener(CART_REFRESH, () => scheduleInit(false));

  window.CartBraceletPreview = { refresh: () => scheduleInit(true) };
})();
